import {
  CompanyId,
  ISO8601DateTimeString,
  ProjectId,
  UserId,
  UUIDv4,
} from "../types";
import { PostBasedPaginatedRequest } from "./shared";

export type AnalyticsId = UUIDv4;
export type PermissionId = UUIDv4; // app had `string`; unified to UUIDv4 (shared-web already used UUIDv4)
export type PermissionV3Id = UUIDv4;
export type PermissionSubjectType = "user" | "role" | "company";

export type AssetsUserInfo = {
  analyticsId: AnalyticsId;
  autodeskId: UserId;
  projectId: ProjectId;
  companyId?: CompanyId | null;
  effectiveLevels: PermissionLevel[];
  isProjectAdmin: boolean;
  abilities: {
    canBulkDeleteAssetMarkups?: boolean;
    canBulkDeleteAssets?: boolean;
    canBulkUpdateAssetMarkups?: boolean;
    canBulkUpdateAssets?: boolean;
    canCreateAssetReferences?: boolean;
    canCreateAssets?: boolean;
    canCreateSystemReferences?: boolean;
    canCreateSystems?: boolean;
    canDeleteAssetMarkups?: boolean;
    canDeleteAssetReferences?: boolean;
    canDeleteAssets?: boolean;
    canDeleteSystemReferences?: boolean;
    canDeleteSystems?: boolean;
    canExportAssets?: boolean;
    canImportAssets?: boolean;
    canManageAssetMetadata?: boolean;
    canManageSystemMetadata?: boolean;
    canModifySystemMemberships?: boolean;
    canUpdateAssetMarkups?: boolean;
    canUpdateAssets?: boolean;
    canUpdateSystems?: boolean;
    canViewAllAssets?: boolean;
    canViewAllSystems?: boolean;
  };
};

export const permissionLevels = Object.freeze([
  "view",
  "edit",
  "create",
  "manage",
] as const);
export type PermissionLevel = (typeof permissionLevels)[number];

export type Permission = {
  id: PermissionId;
  subjectId?: string;
  subjectOxygenId: string;
  subjectType: PermissionSubjectType;
  permissionLevels: PermissionLevel[];
  isDeleted?: boolean;
};

export type NewPermission = Pick<
  Permission,
  "subjectId" | "subjectType" | "permissionLevels"
>;

export type PermissionV3 = {
  createdAt: ISO8601DateTimeString;
  createdBy: UserId;
  updatedAt: ISO8601DateTimeString;
  updatedBy: UserId;
  deletedAt?: ISO8601DateTimeString;
  deletedBy?: UserId;
  id: PermissionV3Id;
  isActive: boolean;
  version: number;
  projectId: ProjectId;
  permissionPolicyType:
    | "view"
    | "edit"
    | "create"
    | "manage"
    | "inherited_permissions";
  subjectType: "user" | "company" | "role" | "project_admins";
  subjectOxygenId: string;
  subjectAcsAdminId: UUIDv4;
  resourceType: "project" | "category" | "system_category";
  resourceId: UUIDv4;
  effect: "allow" | "deny";
};

export type NewPermissionV3 = {
  permissionPolicyType: PermissionV3["permissionPolicyType"];
  resourceType: PermissionV3["resourceType"];
  resourceId: PermissionV3["resourceId"];
  effect?: PermissionV3["effect"];
} & (
  | {
      subjectType: Exclude<PermissionV3["subjectType"], "project_admins">;
      subjectOxygenId: PermissionV3["subjectOxygenId"];
      subjectAcsAdminId?: undefined;
    }
  | {
      subjectType: Exclude<PermissionV3["subjectType"], "project_admins">;
      subjectOxygenId?: undefined;
      subjectAcsAdminId: PermissionV3["subjectAcsAdminId"];
    }
  | {
      subjectType: Extract<PermissionV3["subjectType"], "project_admins">;
      subjectOxygenId?: undefined;
      subjectAcsAdminId?: undefined;
    }
);

export type PermissionsFilterSet = {
  ids?: UUIDv4[];
  subjectTypes?: PermissionV3["subjectType"][];
  subjectOxygenIds?: PermissionV3["subjectOxygenId"][];
  subjectAcsAdminIds?: PermissionV3["subjectAcsAdminId"][];
  resourceTypes?: PermissionV3["resourceType"][];
  resourceIds?: PermissionV3["resourceId"][];
};

export type PermissionsAtomicQuery = {
  entityType: "permissions_atomic_query_v1";
  includeFilters: PermissionsFilterSet;
  excludeFilters: PermissionsFilterSet;
};

export type PermissionsUnionQuery = {
  entityType: "union_query";
  queries: PermissionsAtomicQuery[];
};

export type PermissionsV3ApiQuery =
  | PermissionsUnionQuery
  | PermissionsAtomicQuery;

export type PermissionsV3ApiQueryRequest = PostBasedPaginatedRequest & {
  query: PermissionsV3ApiQuery;
  options?: {
    includeDeleted?: boolean;
  };
};
